import {
  EXERCISE_REQUEST_STATUSES,
  type AdminExerciseRequest,
  type AdminExerciseRequestDetail,
  type ExerciseRequestSort,
  type ExerciseRequestStatusName,
} from '@steado/admin-contract/exercise-requests';
import { useQuery } from '@tanstack/react-query';

import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { ScopePills } from '@/components/table/ScopePills';
import { BadgeCell, TextCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, formatRelative } from '@/lib/format';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';

import { ExerciseRequestDrawer } from './ExerciseRequestDrawer';
import { DRAFT_LABELS, DRAFT_TONES, STATUS_LABELS, STATUS_TONES } from './exercise-request-labels';
import { exerciseRequestsQueryOptions, sortOf, statusOf } from './exercise-requests-query';
import type { ExerciseRequestsSearch } from './exercise-requests-search';

export interface ExerciseRequestsScreenProps {
  readonly search: ExerciseRequestsSearch;
  readonly onSearchChange: (next: ExerciseRequestsSearch) => void;
  /**
   * Opens the create page prefilled from the request's draft. Navigating is
   * the Route's job, so this screen renders without a router.
   */
  readonly onCreateFromDraft: (request: AdminExerciseRequestDetail) => void;
  readonly onOpenExercise?: ((exerciseId: string) => void) | undefined;
}

type Scope = ExerciseRequestStatusName | 'all';

const SCOPE_TITLES: Record<Scope, string> = {
  all: 'All requests',
  open: 'Open requests',
  resolved: 'Resolved',
  rejected: 'Rejected',
};

const SORT_LABELS: Record<ExerciseRequestSort, string> = {
  reach: 'Most people',
  recent: 'Most recent',
};

const COLUMNS: readonly TableColumn[] = [
  { key: 'name', label: 'Exercise', width: '1.6fr' },
  { key: 'equipment', label: 'Equipment', width: '1.2fr' },
  { key: 'users', label: 'People', width: '90px', align: 'right' },
  { key: 'scans', label: 'Scans', width: '90px', align: 'right' },
  { key: 'seen', label: 'Last seen', width: '130px' },
  { key: 'draft', label: 'Draft', width: '130px' },
];

/**
 * Exercises equipment scans offered that the catalog lacks, one row per name.
 *
 * Its own screen beside the exercise library, as food requests sit beside the
 * food catalog: a different endpoint, and a queue rather than a filter. It
 * opens on the open queue in reach order, the gap the most people hit first.
 */
export function ExerciseRequestsScreen({
  search,
  onSearchChange,
  onCreateFromDraft,
  onOpenExercise,
}: ExerciseRequestsScreenProps) {
  const { api } = useAuth();
  const counts = useQuery(scopeCountsQueryOptions(api));
  const page = useQuery(exerciseRequestsQueryOptions(api, search));
  const scope = statusOf(search);
  const sort = sortOf(search);

  const open = (id: string | undefined) => {
    onSearchChange({ ...search, open: id });
  };

  const rows: readonly TableRow[] = (page.data?.items ?? []).map((request) => ({
    id: request.id,
    onOpen: () => {
      open(request.id);
    },
    cells: [
      <TextCell key="name" value={request.name} sub={outcome(request)} weight="medium" />,
      <TextCell key="equipment" value={request.equipmentName ?? '—'} />,
      <TextCell key="users" value={String(request.userCount)} align="right" />,
      <TextCell key="scans" value={String(request.scanCount)} align="right" />,
      <TextCell
        key="seen"
        value={formatRelative(request.lastSeenAt)}
        sub={formatDateTime(request.lastSeenAt)}
      />,
      request.status === 'open' ? (
        <BadgeCell
          key="draft"
          label={DRAFT_LABELS[request.draftStatus]}
          tone={DRAFT_TONES[request.draftStatus]}
        />
      ) : (
        <BadgeCell
          key="draft"
          label={STATUS_LABELS[request.status]}
          tone={STATUS_TONES[request.status]}
          dot
        />
      ),
    ],
  }));

  return (
    <div className="flex max-w-[1100px] flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ScopePills
          label="Request scope"
          scopes={[
            ...EXERCISE_REQUEST_STATUSES.map((status) => ({
              id: status,
              label: STATUS_LABELS[status],
              ...(counts.data === undefined ? {} : { count: counts.data.exerciseRequests[status] }),
            })),
            {
              id: 'all' as const,
              label: 'All',
              ...(counts.data === undefined
                ? {}
                : {
                    count:
                      counts.data.exerciseRequests.open +
                      counts.data.exerciseRequests.resolved +
                      counts.data.exerciseRequests.rejected,
                  }),
            },
          ]}
          value={scope}
          onChange={(next: Scope) => {
            onSearchChange({ ...search, status: next === 'open' ? undefined : next, cursor: undefined });
          }}
        />

        <div role="group" aria-label="Order" className="flex items-center gap-1">
          {(['reach', 'recent'] as const).map((value) => (
            <Button
              key={value}
              type="button"
              size="xs"
              variant={sort === value ? 'secondary' : 'ghost'}
              aria-pressed={sort === value}
              onClick={() => {
                onSearchChange({ ...search, sort: value === 'reach' ? undefined : value, cursor: undefined });
              }}
            >
              {SORT_LABELS[value]}
            </Button>
          ))}
        </div>
      </div>

      <p className="text-[13px] leading-relaxed text-muted-foreground">
        Exercises gym equipment scans offered that the catalog does not have. Users were shown the
        model&apos;s own steps, marked as not yet reviewed; resolving a request to a catalog row gives every
        later scan of that name the reviewed row instead.
      </p>

      {page.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{page.error.message}</AlertDescription>
        </Alert>
      ) : null}

      <DataTable
        caption="Exercise requests"
        title={SCOPE_TITLES[scope]}
        count={rows.length === 0 ? undefined : `showing ${String(rows.length)}`}
        columns={COLUMNS}
        rows={rows}
        isLoading={page.isPending}
        emptyTitle="Nothing waiting"
        emptyBody="No exercise request in this scope. For the open queue that is the healthy state: scans are finding their exercises in the catalog."
        footer={
          page.data?.pagination.hasMore === true ? (
            <div className="flex items-center justify-end border-t border-border px-[18px] py-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onSearchChange({ ...search, cursor: page.data?.pagination.nextCursor ?? undefined });
                }}
              >
                Next page
              </Button>
            </div>
          ) : undefined
        }
      />

      {search.open === undefined ? null : (
        <ExerciseRequestDrawer
          requestId={search.open}
          onClose={() => {
            open(undefined);
          }}
          onCreateFromDraft={onCreateFromDraft}
          onOpenExercise={onOpenExercise}
        />
      )}
    </div>
  );
}

function outcome(request: AdminExerciseRequest): string | null {
  if (request.status === 'resolved') {
    return request.resolvedExercise === null
      ? 'Resolved to an exercise since deleted'
      : `Resolved to ${request.resolvedExercise.name}`;
  }
  if (request.status === 'rejected') {
    return request.rejectionReason === null ? 'Rejected' : `Rejected: ${request.rejectionReason}`;
  }
  return null;
}
